import type {
  IAddressSegment,
  IProject,
  Screen,
} from '@/store/organization/projectSlice';

import { GeoMode, screenTranslate } from '@/store/organization/projectSlice';

/**
 * Warnings about devices a chosen address cannot cover.
 *
 * Two situations are reported separately on purpose: a device the address does
 * not serve at all, and one that exists but is currently switched off. They look
 * the same to a user staring at an empty report, so the wording has to tell them
 * apart. These are warnings only — saving stays allowed, the scheduler simply
 * skips the uncovered device.
 */
export function getAddressWarnings(
  segments: IAddressSegment[],
  selectedSlugs: string[],
  selectedScreens: Screen[]
): string[] {
  const warnings: string[] = [];

  for (const slug of selectedSlugs) {
    const segment = segments.find((s) => s.address_slug === slug);
    if (!segment) {
      warnings.push(`Адрес «${slug}» больше не доступен`);
      continue;
    }

    const missing = selectedScreens.filter(
      (screen) => !segment.devices.some((d) => d.screen === screen)
    );
    const inactive = selectedScreens.filter((screen) =>
      segment.devices.some((d) => d.screen === screen && !d.is_active)
    );

    if (missing.length) {
      warnings.push(
        `«${segment.address_name}»: не обслуживается ${missing
          .map((s) => screenTranslate[s])
          .join(', ')}`
      );
    }
    if (inactive.length) {
      warnings.push(
        `«${segment.address_name}»: сейчас неактивно ${inactive
          .map((s) => screenTranslate[s])
          .join(', ')}`
      );
    }
  }

  return warnings;
}

/**
 * Which values to send as the report's region filter.
 *
 * ClickHouse stores the address *name* in `city` for address-mode projects, so
 * their reports must be filtered by name — falling back to `marketplace.region`
 * would filter on 'Москва' and return nothing at all.
 */
export function resolveProjectRegions(
  project: IProject | undefined,
  addressSegments: IAddressSegment[]
): string[] {
  if (project?.geo_mode === GeoMode.Address) {
    const names = (project.address_slugs ?? [])
      .map(
        (slug) =>
          addressSegments.find((s) => s.address_slug === slug)?.address_name
      )
      .filter((name): name is string => Boolean(name));
    if (names.length) return names;
  }

  const region = project?.marketplace?.region;

  return region?.length ? region : ['Москва'];
}
